import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Sparkles, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const AdminBriefDigestReviews = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [weekFilter, setWeekFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (weekFilter.trim()) params.weekKey = weekFilter.trim();
      const res = await axios.get('/api/admin/brief-digest-reviews', {
        params,
        withCredentials: true
      });
      if (res.data.success) {
        setItems(res.data.items || []);
        setWeeks(res.data.weeks || []);
      } else {
        toast.error(res.data.error || 'Failed to load');
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access denied.');
        navigate('/trend-spotter');
      } else {
        toast.error(e.response?.data?.message || 'Failed to load reviews');
      }
    } finally {
      setLoading(false);
    }
  }, [weekFilter, navigate]);

  useEffect(() => {
    if (user && user.email && user.email.toLowerCase() !== 'info@showstop.ai') {
      toast.error('Access denied. Admin only.');
      navigate('/trend-spotter');
      return;
    }
    load();
  }, [user, navigate, load]);

  const runEval = async (itemId) => {
    try {
      setEvaluatingId(itemId);
      const res = await axios.post(
        '/api/admin/brief-digest-reviews/evaluate',
        { itemId },
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(`Eval: ${res.data.eval_score}/10`);
        load();
      } else {
        toast.error(res.data.error || 'Eval failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Eval failed');
    } finally {
      setEvaluatingId(null);
    }
  };

  const runBatchMissing = async () => {
    try {
      setBatchRunning(true);
      const body = {};
      if (weekFilter.trim()) body.weekKey = weekFilter.trim();
      body.limit = 25;
      const res = await axios.post('/api/admin/brief-digest-reviews/evaluate-missing', body, {
        withCredentials: true
      });
      if (res.data.success) {
        toast.success(`Evaluated ${res.data.evaluated} brief(s)`);
        if (res.data.errors?.length) {
          console.warn('Eval batch errors', res.data.errors);
          toast.error(`${res.data.errors.length} row(s) failed — see console`);
        }
        load();
      } else {
        toast.error(res.data.error || 'Batch failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Batch failed');
    } finally {
      setBatchRunning(false);
    }
  };

  const toggleRow = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[95rem] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-zinc-300" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Sparkles className="h-8 w-8 text-teal-600" />
              Friday brief reviews
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={weekFilter}
              onChange={(e) => setWeekFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All recent weeks</option>
              {weeks.map((w) => (
                <option key={w.week_key} value={w.week_key}>
                  {w.week_key} ({w.cnt})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-sm hover:bg-gray-50 dark:hover:bg-zinc-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={runBatchMissing}
              disabled={batchRunning || loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {batchRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Eval missing (batch)
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-zinc-400 mb-4 max-w-4xl">
          Line items from Friday sends (teaser copy + full shots stored for review). Star ratings from email feedback; optional LLM scores (1–10).
          Filter by week or run batch eval on rows without a score yet.
        </p>

        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
            </div>
          ) : items.length === 0 ? (
            <div className="px-6 py-16 text-center text-gray-500 dark:text-zinc-400">
              No brief line items yet. They are saved when Friday cron sends (after migration) or when you use CRM “Send Fri brief”.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400">
                  <tr>
                    <th className="px-3 py-3 w-8" />
                    <th className="px-3 py-3">Week</th>
                    <th className="px-3 py-3">User</th>
                    <th className="px-3 py-3">Slot</th>
                    <th className="px-3 py-3">Trend</th>
                    <th className="px-3 py-3">Email rating</th>
                    <th className="px-3 py-3">Eval</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-zinc-700">
                  {items.map((row) => {
                    const open = expanded[row.item_id];
                    return (
                      <React.Fragment key={row.item_id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-zinc-800/50 align-top">
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={() => toggleRow(row.item_id)}
                              className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-700"
                              aria-label={open ? 'Collapse' : 'Expand'}
                            >
                              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-gray-800 dark:text-zinc-200">
                            {row.week_key}
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-gray-900 dark:text-white font-medium">{row.user_email || row.user_id}</div>
                            {row.user_name ? (
                              <div className="text-xs text-gray-500 dark:text-zinc-400">{row.user_name}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-3 text-gray-700 dark:text-zinc-300">{row.slot_index + 1}</td>
                          <td className="px-3 py-3 max-w-[14rem]">
                            <div className="text-gray-900 dark:text-white line-clamp-2">{row.trend_name}</div>
                          </td>
                          <td className="px-3 py-3">
                            {row.feedback_rating != null ? (
                              <span className="text-amber-600 dark:text-amber-400 font-medium">
                                {row.feedback_rating} / 5
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {row.eval_score != null ? (
                              <div>
                                <span className="text-teal-600 dark:text-teal-400 font-medium">{row.eval_score} / 10</span>
                                {row.eval_model ? (
                                  <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[10rem]" title={row.eval_model}>
                                    {row.eval_model}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => runEval(row.item_id)}
                              disabled={evaluatingId !== null}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                            >
                              {evaluatingId === row.item_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3" />
                              )}
                              Run eval
                            </button>
                          </td>
                        </tr>
                        {open ? (
                          <tr className="bg-gray-50/80 dark:bg-zinc-800/40">
                            <td colSpan={8} className="px-6 py-4 text-gray-700 dark:text-zinc-300 space-y-3">
                              <div>
                                <span className="text-xs font-semibold text-gray-500 uppercase">Brand</span>
                                <p className="mt-1">
                                  {row.brand_name || '—'}
                                  {row.brand_description ? (
                                    <span className="block text-sm text-gray-500 dark:text-zinc-400 mt-1">
                                      {row.brand_description}
                                    </span>
                                  ) : null}
                                </p>
                              </div>
                              {row.trend_summary ? (
                                <div>
                                  <span className="text-xs font-semibold text-gray-500 uppercase">Trend summary</span>
                                  <p className="mt-1 whitespace-pre-wrap text-sm">{row.trend_summary}</p>
                                </div>
                              ) : null}
                              {row.brief_summary ? (
                                <div>
                                  <span className="text-xs font-semibold text-gray-500 uppercase">Brief summary (email teaser)</span>
                                  <p className="mt-1 whitespace-pre-wrap text-sm">{row.brief_summary}</p>
                                </div>
                              ) : null}
                              <div>
                                <span className="text-xs font-semibold text-gray-500 uppercase">Angle</span>
                                <p className="mt-1 whitespace-pre-wrap">{row.angle || '—'}</p>
                              </div>
                              <div>
                                <span className="text-xs font-semibold text-gray-500 uppercase">Shots (full)</span>
                                <ul className="mt-1 list-decimal list-inside text-sm">
                                  {Array.isArray(row.shots) && row.shots.length
                                    ? row.shots.map((s, i) => <li key={i}>{String(s)}</li>)
                                    : '—'}
                                </ul>
                              </div>
                              <div>
                                <span className="text-xs font-semibold text-gray-500 uppercase">Caption</span>
                                <p className="mt-1 whitespace-pre-wrap text-sm">{row.caption || '—'}</p>
                              </div>
                              <div>
                                <span className="text-xs font-semibold text-gray-500 uppercase">Notes</span>
                                <p className="mt-1 whitespace-pre-wrap text-sm">{row.notes || '—'}</p>
                              </div>
                              {row.feedback_text ? (
                                <div>
                                  <span className="text-xs font-semibold text-gray-500 uppercase">Feedback text</span>
                                  <p className="mt-1 whitespace-pre-wrap text-sm">{row.feedback_text}</p>
                                </div>
                              ) : null}
                              {row.eval_rationale ? (
                                <div>
                                  <span className="text-xs font-semibold text-gray-500 uppercase">Eval rationale</span>
                                  <p className="mt-1 whitespace-pre-wrap text-sm">{row.eval_rationale}</p>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBriefDigestReviews;
